<script lang="ts">
  import { getUiclContext, useActions } from "#lib/internal/helpers"
  import { cn } from "#lib/utils"
  import { Locked } from "@tastyworks/icons"
  import type { Snippet } from "svelte"
  import type { HTMLInputAttributes } from "svelte/elements"
  import InputContainer from "./InputContainer.svelte"
  import { inputFieldVariants } from "./variants"

  interface Props extends Omit<HTMLInputAttributes, "value"> {
    class?: string
    value?: HTMLInputAttributes["value"]
    locked?: boolean
    disabled?: boolean
    error?: boolean
    success?: boolean
    iconClass?: string
    ref?: HTMLInputElement | null
    containerClass?: string
    leftIcon?: Snippet
    rightIcon?: Snippet
    button?: Snippet
    use?: any
    [key: string]: any
  }

  let {
    class: className = undefined,
    value = $bindable(""),
    locked = false,
    disabled = false,
    error = false,
    success = undefined,
    iconClass = undefined,
    ref = $bindable(),
    containerClass = undefined,
    leftIcon,
    rightIcon,
    button,
    use = [],
    ...restProps
  }: Props = $props()

  const v2 = !!getUiclContext()?.v2
</script>

<InputContainer class={containerClass}>
  {#if leftIcon}
    <div class={cn("pointer-events-none absolute left-3 flex items-center")}>
      {@render leftIcon()}
    </div>
  {/if}

  <input
    class={cn(
      inputFieldVariants({
        error,
        success,
        v2,
      }),
      leftIcon && "indent-7",
      className
    )}
    use:useActions={use}
    bind:value
    bind:this={ref}
    disabled={locked || disabled}
    aria-invalid={error}
    data-value={value ?? ""}
    {...restProps}
  />

  {#if rightIcon || locked}
    <div
      class={cn(
        "pointer-events-none absolute left-auto right-3 flex items-center"
      )}
    >
      {#if locked}
        <Locked class={cn("w-5 text-icon-general-disabled", iconClass)} />
      {:else if rightIcon}
        {@render rightIcon()}
      {/if}
    </div>
  {/if}
  {#if !locked && button}
    {@render button()}
  {/if}
</InputContainer>

<style lang="postcss">
  /* hide arrow buttons when type is number */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }

  /* hide password reveal in favor of our own */
  input::-ms-reveal,
  input::-ms-clear {
    display: none;
  }
</style>
